import { Observable } from 'rxjs/Observable';
import { ActionOptionsArgs } from '../../../core/store/actions/base-actions';
import { isInArray } from '../../../core/util/is-in-array';
import { AccessGroup } from '../access-group/access-group.model';
import { AccountObjectStoreFacade } from '../account-object/account-object.store-facade';
import { ContactType } from '../contact-type/contact-type.model';
import { JobStatus } from '../job-status/job-status.model';
import { JobType } from '../job-type/job-type.model';
import { LookupType } from '../lookup-type/lookup-type.model';
import { Qry } from '../query-facade';
import { TaskType } from '../task-type/task-type.model';
import { User } from '../user/user.model';
import * as actions from './membership.actions';
import { Membership } from './membership.model';

export class MembershipStoreFacade extends AccountObjectStoreFacade<Membership> {

    getAll(): Observable<Array<Membership>> {
        return this.select(Qry.getInstance().membership.byActiveAccount);
    }

    getForAllAccounts(): Observable<Array<Membership>> {
        return this.select(Qry.getInstance().membership.items);
    }

    getAllByAccount(accountId: string): Observable<Array<Membership>> {
        return this.select(Qry.getInstance().membership.byAccount(accountId));
    }

    getActive(): Observable<Membership> {
        return this.select(Qry.getInstance().membership.selected);
    }

    isOwner(membership: Membership): Observable<boolean> {
        return this.select(Qry.getInstance().helper.isOwner(membership.id));
    }

    isAdmin(membership: Membership): Observable<boolean> {
        return Observable.of(membership.admin);
    }

    isActiveUserOwner(): Observable<boolean> {
        return this.select(Qry.getInstance().helper.isActiveUserOwner);
    }

    isActiveUserAdmin(): Observable<boolean> {
        return this.select(Qry.getInstance().membership.isActiveUserAdmin);
    }

    create(membership: Membership): void {
        this.dispatch(new actions.SubmitAction(membership));
    }

    insert(membership: Membership): void {
        this.dispatch(new actions.SubmitAction(membership, {persistable: true, restful: false}));
    }

    remove(membership: Membership, options?: ActionOptionsArgs): void {
        console.error('WHY ARE YOU TRYING TO DELETE THE MEMBERSHIP?!?!?!?!');
        // tslint:disable-next-line:no-debugger
        debugger;
        // this.dispatch(new actions.RemoveAction(membership, options));
    }

    removeByUser(user: User): void {
        this.removeBy(membership => membership.user === user.id);
    }

    update(membership: Membership): void {
        this.dispatch(new actions.SubmitAction(membership));
    }

    attachAccessGroup(membership: Membership, accessGroup: AccessGroup, options?: ActionOptionsArgs): void {

        if (!accessGroup || !membership || isInArray(accessGroup.id, membership.accessGroups)) {
            return;
        }

        membership = new Membership({
            ...membership,
            accessGroups: [...membership.accessGroups, accessGroup.id]
        });

        this.dispatch(new actions.SubmitAction(membership, options));
    }

    detachAccessGroup(membership: Membership, accessGroup: AccessGroup, options?: ActionOptionsArgs): void {

        if (!accessGroup || !membership || !isInArray(accessGroup.id, membership.accessGroups)) {
            return;
        }

        membership = new Membership({
            ...membership,
            accessGroups: membership.accessGroups.filter(id => id !== accessGroup.id)
        });

        this.dispatch(new actions.SubmitAction(membership, options));
    }

    attachContactType(membership: Membership, contactType: ContactType, options?: ActionOptionsArgs): void {

        if (!contactType || !membership || isInArray(contactType.id, membership.contactTypes)) {
            return;
        }

        membership = new Membership({
            ...membership,
            contactTypes: [...membership.contactTypes, contactType.id]
        });

        this.dispatch(new actions.SubmitAction(membership, options));
    }

    detachContactType(membership: Membership, contactType: ContactType, options?: ActionOptionsArgs): void {

        if (!contactType || !membership || !isInArray(contactType.id, membership.contactTypes)) {
            return;
        }

        membership = new Membership({
            ...membership,
            contactTypes: membership.contactTypes.filter(id => id !== contactType.id)
        });

        this.dispatch(new actions.SubmitAction(membership, options));
    }

    attachLookupType(membership: Membership, lookupType: LookupType, options?: ActionOptionsArgs): void {

        if (!lookupType || !membership || isInArray(lookupType.id, membership.lookupTypes)) {
            return;
        }

        membership = new Membership({
            ...membership,
            lookupTypes: [...membership.lookupTypes, lookupType.id]
        });

        this.dispatch(new actions.SubmitAction(membership, options));
    }

    detachLookupType(membership: Membership, lookupType: LookupType, options?: ActionOptionsArgs): void {

        if (!lookupType || !membership || !isInArray(lookupType.id, membership.lookupTypes)) {
            return;
        }

        membership = new Membership({
            ...membership,
            lookupTypes: membership.lookupTypes.filter(id => id !== lookupType.id)
        });

        this.dispatch(new actions.SubmitAction(membership, options));
    }

    attachTaskType(membership: Membership, taskType: TaskType, options?: ActionOptionsArgs): void {

        if (!taskType || !membership || isInArray(taskType.id, membership.taskTypes)) {
            return;
        }

        membership = new Membership({
            ...membership,
            taskTypes: [...membership.taskTypes, taskType.id]
        });

        this.dispatch(new actions.SubmitAction(membership, options));
    }

    detachTaskType(membership: Membership, taskType: TaskType, options?: ActionOptionsArgs): void {

        if (!taskType || !membership || !isInArray(taskType.id, membership.taskTypes)) {
            return;
        }

        membership = new Membership({
            ...membership,
            taskTypes: membership.taskTypes.filter(id => id !== taskType.id)
        });

        this.dispatch(new actions.SubmitAction(membership, options));
    }

    attachJobType(membership: Membership, jobType: JobType, options?: ActionOptionsArgs): void {

        if (!jobType || !membership || isInArray(jobType.id, membership.jobTypes)) {
            return;
        }

        membership = new Membership({
            ...membership,
            jobTypes: [...membership.jobTypes, jobType.id]
        });

        this.dispatch(new actions.SubmitAction(membership, options));
    }

    detachJobType(membership: Membership, jobType: JobType, options?: ActionOptionsArgs): void {

        if (!jobType || !membership || !isInArray(jobType.id, membership.jobTypes)) {
            return;
        }

        membership = new Membership({
            ...membership,
            jobTypes: membership.jobTypes.filter(id => id !== jobType.id)
        });

        this.dispatch(new actions.SubmitAction(membership, options));
    }

    attachJobStatus(membership: Membership, jobStatus: JobStatus, options?: ActionOptionsArgs): void {

        if (!jobStatus || !membership || isInArray(jobStatus.id, membership.jobStatuses)) {
            return;
        }

        membership = new Membership({
            ...membership,
            jobStatuses: [...membership.jobStatuses, jobStatus.id]
        });

        this.dispatch(new actions.SubmitAction(membership, options));
    }

    detachJobStatus(membership: Membership, jobStatus: JobStatus, options?: ActionOptionsArgs): void {

        if (!jobStatus || !membership || !isInArray(jobStatus.id, membership.jobStatuses)) {
            return;
        }

        membership = new Membership({
            ...membership,
            jobStatuses: membership.jobStatuses.filter(id => id !== jobStatus.id)
        });

        this.dispatch(new actions.SubmitAction(membership, options));
    }

    enqueueRefresh(): void {
        this.getActive()
            .take(1)
            .subscribe(membership => {
                this.dispatch(new actions.RefreshAction(membership));
            });
    }
}



// WEBPACK FOOTER //
// ./src/vas/business-objects/membership/membership.store-facade.ts